import styles from './search-bar.module.scss';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { ContactListContext, GetCurrentCompany, SelectedContactUpdateContext } from '../customer-context/customer-context';
import HeaderSearch from '../header-search/header-search';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import { GetRoles } from '../TokenContext';
import { Box, Container, Icon, SecondaryButton, Option, VirtualizedSelect, checkUserAccess, Roles, IContactType, TextField, SnackBarContext } from '@frontend-workspace/react-shared-lib';
import { GPTMessage } from '../gpt-helper/gpt-helper';
import { useTranslation } from 'react-i18next';
import { CompanySalesforceLink } from '../salesforce-navigation/salesforce-navigation';

/* eslint-disable-next-line */
export interface SearchBarProps { salesforceBaseUrl?: string }

export function SearchBar(props: SearchBarProps) {
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const [newCompanyDialogOpen, setnewCompanyDialogOpen] = useState<boolean>(false);
  const [isLoadingGPT, setIsLoadingGPT] = useState(false);
  const currentCompany = GetCurrentCompany();
  const navigate = useNavigate();
  const companyList = useContext<IContactType[]>(ContactListContext);
  const setSelectedCompany = useContext(SelectedContactUpdateContext);
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [messages, setMessages] = useState<Array<GPTMessage>>([]);
  const { data, isLoading } = useGetRequestNewHook(`Accounts`, []);

  const handleNewCompanyToggle = () => setnewCompanyDialogOpen(!newCompanyDialogOpen);

  const userOptions: Array<Option> = data.map((ua: any) => {
    let email: string = ua.email;
    //Filter out the deleted guid from email
    const regex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
    const match = email.match(regex);
    if (match) {
      email = match[0];
    }
    return {
      id: ua.email,
      label: `${email} (${ua.companyNumber}) ${ua.isActive ? "" : `(${t("standard.deleted")})`}`
    }
  });
  const handleResponse = (response: any) => {
    setIsLoadingGPT(false);
    setMessages(response.messages);

    const lastMessage = response.messages[response.messages.length - 1];

    if (lastMessage.navigationUrl) {
      navigate(lastMessage.navigationUrl);
      setSelectedCompany(lastMessage.companyNumber);
    }
    else if (lastMessage.content.includes(t("standard.sendingYouTo"))) {
      const content: string = lastMessage.content;
      const lastMessageNavigationUrl = content.slice(content.indexOf("/"));

      const match = content.match(/\d+/); // Matches any sequence of digits in the string
      const number = match ? parseInt(match[0], 10) : null;
      navigate(lastMessageNavigationUrl);
      setSelectedCompany(number);
    }

    setSnackbarMessage({ content: lastMessage.content, type: "info", duration: 60000 });
  }
  const handleUserChange = (email: string) => {
    const user = data.find((d: any) => d.email === email);
    const company = companyList.find(c => c.number === user.companyNumber);
    setSelectedCompany(company);
    navigate(`customers/${user.companyNumber}/details`);
  };
  const handleGPTSearch = (e: React.KeyboardEvent<HTMLDivElement>, optionList: Option[]) => {
    const target = e.target as HTMLInputElement;
    if (e.key === 'Enter') {
      const createLastMessage = (message: string) => {
        return {
          role: "user",
          content: message,
          hideInChat: false
        }
      }
      const sentMessages = [...messages, createLastMessage(target.value)];
      setIsLoadingGPT(true);
      makeRequest("GPT/helper/page", "POST", {
        messages: sentMessages,
        currentUrl: window.location.href,
        companyNumber: currentCompany?.number
      }).then(handleResponse).catch((error) => {
        setIsLoadingGPT(false);
        setSnackbarMessage({ content: t("searchBar.somethingWentWrong"), type: "error", duration: 60000 });
      });

    }
  };

  const salesforceLink = currentCompany && props.salesforceBaseUrl
    ? <CompanySalesforceLink key={currentCompany.number}
        companyNumber={currentCompany.number} baseUrl={props.salesforceBaseUrl} />
    : null;
  const pathName = window.location.pathname;
  const isWorkingOnContract = pathName.includes("/contracts/");
  const isAdminCourse = pathName.includes("/course-admin/");

  return <>
    {!isAdminCourse &&
      <Box className='nav-box d-flex align-items-center'>
        <Container className='fluid' maxWidth={false} disableGutters>
          <Row>
            {isWorkingOnContract ?
              <Col md={12} xl={6} xxl={5} className='d-flex align-items-center justify-content-start gap-2'>
                {currentCompany?.name &&
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <TextField
                      className='my-1 my-xxl-0'
                      defaultValue={`${currentCompany?.name} (${currentCompany?.number})`}
                      disabled
                      fullWidth
                    />
                  </div>
                }
                {salesforceLink}
              </Col> :
              <>
                <Col md={12} xl={6} xxl={5}>
                  <div className='my-1 my-xxl-0 d-flex align-items-center gap-2'>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <HeaderSearch />
                    </div>
                    {salesforceLink}
                  </div>
                </Col>
                <Col md={12} xl={6} xxl={3}>
                  {isLoadingGPT ? <div>{t("searchBar.working")}</div> :
                    <div className='my-1 my-xxl-0'>
                      <VirtualizedSelect
                        options={userOptions}
                        handleSelect={handleUserChange}
                        onKeyDown={handleGPTSearch}
                        placeholder={isLoading ? t("searchBar.retrievingFocusAccounts") || "" : t("searchBar.findCompanyForFocusAccount") || ""}
                      />
                    </div>
                  }
                </Col>
              </>
            }
          </Row>
        </Container>
      </Box>
    }
  </>
}

export default SearchBar;